/** The pieces Dag, Uke and Måned's figures share: the fold, the «Diagram» button, the «Mangler» line, storage. */
import { T } from '@/i18n';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import css from './WeekFigures.module.css';
import type { Miss } from './figures';

const stored = <V,>(key: string, fallback: V): V => {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : (JSON.parse(v) as V);
  } catch {
    return fallback;
  }
};
/** a choice remembered in this browser */
export function useStored<V>(key: string, fallback: V) {
  const [v, setV] = useState<V>(() => stored(key, fallback));
  const set = (n: V) => {
    setV(n);
    try {
      localStorage.setItem(key, JSON.stringify(n));
    } catch {}
  };
  return [v, set] as const;
}

/** «vi bruker ikke dette», per browser and company, the same list in every period */
// ponytail: a company setting when the server keeps one
export const useNotUsed = (tenantId: number | undefined) =>
  useStored<string[]>(`vaktplan.notUsed.${tenantId ?? 0}`, []);

export function FoldButton({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={css.fold} aria-expanded={open} onClick={onClick}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
      </svg>
      {label}
    </button>
  );
}

export function ChartsButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={css.cbtn}
      aria-expanded={open}
      onClick={onClick}
      data-testid="figures-charts"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="12" width="4" height="8" rx="1" />
        <rect x="10" y="7" width="4" height="13" rx="1" />
        <rect x="17" y="3" width="4" height="17" rx="1" />
      </svg>
      {open ? T('Skjul diagram') : T('Diagram')}
    </button>
  );
}

/** «Mangler: Bemanningsbehov × …»: each word goes where it is filled in; × hides it for good (not the wage) */
export function MissLine({
  miss,
  onHide,
  onSales,
}: {
  miss: Miss[];
  onHide: (k: string) => void;
  /** where the sales are typed on this page; without it the link goes to the week */
  onSales?: () => void;
}) {
  if (!miss.length) return null;
  return (
    <span className={css.miss} data-testid="figures-missing">
      <span>{T('Mangler')}:</span>
      {miss.map(({ k, t, how, href }) => (
        <span key={k} className={css.mi}>
          {href.startsWith('#') && onSales ? (
            <a
              className="link"
              href={href}
              title={how}
              onClick={(e) => {
                e.preventDefault();
                onSales();
              }}
            >
              {t}
            </a>
          ) : (
            <Link className="link" to={href.startsWith('#') ? '/vaktplan' : href} title={how}>
              {t}
            </Link>
          )}
          {k !== 'wages' && (
            <button
              type="button"
              className={css.x}
              title={T('Vi bruker ikke dette')}
              aria-label={`${T('Vi bruker ikke dette')}: ${t}`}
              onClick={() => onHide(k)}
            >
              ×
            </button>
          )}
        </span>
      ))}
    </span>
  );
}
